import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface AdminLog {
  id: string;
  admin_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const actionColors: Record<string, string> = {
  '调整积分': 'bg-rose-50 text-rose-600',
  '调整余额': 'bg-emerald-50 text-emerald-600',
  '重置密码': 'bg-amber-50 text-amber-600',
  '重置支付密码': 'bg-violet-50 text-violet-600',
  '新增会员': 'bg-sky-50 text-sky-600',
  '发货': 'bg-teal-50 text-teal-600',
  '退款': 'bg-orange-50 text-orange-600',
  '修改商品': 'bg-gray-100 text-gray-600',
  '删除商品': 'bg-red-50 text-red-600',
};

const actionIcons: Record<string, string> = {
  '调整积分': 'ri-copper-coin-line',
  '调整余额': 'ri-wallet-3-line',
  '重置密码': 'ri-lock-password-line',
  '重置支付密码': 'ri-shield-keyhole-line',
  '新增会员': 'ri-user-add-line',
  '发货': 'ri-truck-line',
  '退款': 'ri-refund-2-line',
  '修改商品': 'ri-edit-line',
  '删除商品': 'ri-delete-bin-line',
};

const AdminLogsPage = () => {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('全部');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('admin_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    setLogs((data as AdminLog[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const allActions = ['全部', ...Array.from(new Set(logs.map(l => l.action)))];

  const filtered = logs.filter(l => {
    const matchAction = filterAction === '全部' || l.action === filterAction;
    const q = search.trim().toLowerCase();
    const matchSearch = !q ||
      l.action.toLowerCase().includes(q) ||
      (l.target_id ?? '').toLowerCase().includes(q) ||
      (l.target_type ?? '').toLowerCase().includes(q) ||
      JSON.stringify(l.detail ?? {}).toLowerCase().includes(q);
    return matchAction && matchSearch;
  });

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const getDetailText = (log: AdminLog) => {
    if (!log.detail) return '';
    const d = log.detail;
    const parts: string[] = [];
    if (d.memberName) parts.push(`会员：${d.memberName}`);
    if (d.delta !== undefined) parts.push(`变动：${Number(d.delta) > 0 ? '+' : ''}${d.delta}`);
    if (d.newPoints !== undefined) parts.push(`调整后积分：${d.newPoints}`);
    if (d.newBalance !== undefined) parts.push(`调整后余额：¥${Number(d.newBalance).toFixed(2)}`);
    if (d.orderNo) parts.push(`订单：${d.orderNo}`);
    if (d.trackingNo) parts.push(`快递：${d.trackingNo}`);
    if (d.remark) parts.push(`备注：${d.remark}`);
    return parts.join('  ·  ') || JSON.stringify(d);
  };

  // 今日操作数
  const todayCount = logs.filter(l => {
    const d = new Date(l.created_at);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <AdminLayout title="管理日志">
      {/* 统计 */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        {[
          { label: '今日操作', value: todayCount, icon: 'ri-calendar-check-line', color: 'text-rose-500 bg-rose-50' },
          { label: '总记录数', value: logs.length, icon: 'ri-file-list-3-line', color: 'text-sky-500 bg-sky-50' },
          { label: '操作类型', value: allActions.length - 1, icon: 'ri-layout-grid-line', color: 'text-amber-500 bg-amber-50' },
          { label: '当前筛选', value: filtered.length, icon: 'ri-filter-line', color: 'text-emerald-500 bg-emerald-50' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className="text-xl font-bold text-gray-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 工具栏 */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜索操作内容..."
            className="text-sm outline-none w-48 placeholder-gray-400"
          />
          {search && <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500 cursor-pointer"><i className="ri-close-line text-sm" /></button>}
        </div>
        <button onClick={fetchLogs} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-white border border-gray-200 text-gray-600 hover:border-rose-300 transition-all">
          <i className="ri-refresh-line text-sm" />刷新
        </button>
        <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200 flex-wrap">
          {allActions.slice(0, 8).map(a => (
            <button
              key={a}
              onClick={() => setFilterAction(a)}
              className={`px-3 py-1.5 rounded-md text-xs cursor-pointer whitespace-nowrap transition-all ${filterAction === a ? 'bg-rose-500 text-white font-medium' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* 日志列表 */}
      <div className="bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <i className="ri-file-list-3-line text-4xl text-gray-200 block mb-3" />
            <p className="text-gray-400 text-sm">
              {logs.length === 0 ? '暂无操作日志记录' : '没有匹配的日志'}
            </p>
            {logs.length === 0 && (
              <p className="text-xs text-gray-300 mt-1">后台的操作将自动记录到此处</p>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">操作类型</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">操作对象</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">详细内容</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">操作时间</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log, i) => (
                <tr key={log.id} className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${actionColors[log.action] ?? 'bg-gray-100 text-gray-500'}`}>
                        <i className={`${actionIcons[log.action] ?? 'ri-settings-3-line'} text-sm`} />
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${actionColors[log.action] ?? 'bg-gray-100 text-gray-500'}`}>
                        {log.action}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-700 font-medium">{log.target_type ?? '-'}</p>
                    <p className="text-xs text-gray-400 mt-0.5 font-mono">{log.target_id ?? ''}</p>
                  </td>
                  <td className="px-5 py-4 max-w-xs">
                    <p className="text-sm text-gray-600 line-clamp-2">{getDetailText(log) || '-'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400 whitespace-nowrap">{formatTime(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="mt-4 text-xs text-gray-400 text-right">共 {filtered.length} 条记录</div>
      )}
    </AdminLayout>
  );
};

export default AdminLogsPage;
